import { UseQueryOptions } from "react-query";
import { useFormeBackendApi } from "@modules/forme/queries";
import {
  Scenario,
  QuickDate,
  FinancialGoal,
  ExpenseTimeline,
  FinancialPlanYear,
  MonteCarloResult,
} from "./types";

const BASE_URL = "/v1/financial-plans";

type FinancialPlanQueryParams = {
  scenario: Scenario;
  quickDate?: QuickDate;
  date?: string;
};
type FinancialPlanQueryOptions = UseQueryOptions;

const getDate = (params: FinancialPlanQueryParams) =>
  params.date ?? params.quickDate ?? "most-recent";

const useFinancialPlanGoals = (
  params: FinancialPlanQueryParams,
  options?: FinancialPlanQueryOptions
) => {
  return useFormeBackendApi<FinancialGoal[]>({
    method: "GET",
    path: `${BASE_URL}/${params.scenario}/goals?date=${getDate(params)}`,
    options,
    dataKey: "data.values",
  });
};

const useFinancialPlanCombinedDetail = (
  params: FinancialPlanQueryParams,
  options?: FinancialPlanQueryOptions
) => {
  return useFormeBackendApi<{
    data: { years: FinancialPlanYear[] };
    timestamp: string;
  }>({
    method: "GET",
    path: `${BASE_URL}/${params.scenario}/combined-detail?date=${getDate(
      params
    )}`,
    options,
  });
};

const useFinancialPlanMonteCarlo = (
  params: FinancialPlanQueryParams,
  options?: FinancialPlanQueryOptions
) => {
  return useFormeBackendApi<MonteCarloResult>({
    method: "GET",
    path: `${BASE_URL}/${params.scenario}/monte-carlo?date=${getDate(params)}`,
    options,
    dataKey: "data.needsWantsAndWishes",
  });
};

const useFinancialPlanExpenseTimeline = (
  params: FinancialPlanQueryParams,
  options?: FinancialPlanQueryOptions
) => {
  return useFormeBackendApi<ExpenseTimeline>({
    method: "GET",
    path: `${BASE_URL}/${params.scenario}/expense-timeline?date=${getDate({
      ...params,
      quickDate: params.quickDate ?? "most-recent",
    })}`,
    options,
    dataKey: "data",
  });
};

export {
  useFinancialPlanGoals,
  useFinancialPlanCombinedDetail,
  useFinancialPlanMonteCarlo,
  useFinancialPlanExpenseTimeline,
};
